const API_BASE_URL = ''; // React 앱과 Go 서버가 같은 도메인에서 제공될 경우 빈 문자열

export const getViewedCompanyIDFromPath = (path = window.location.pathname) => {
  const match = path.match(/^\/company\/([0-9]{3})(?:\/|$)/);
  return match ? match[1] : '';
};

export const buildAuthHeaders = (headers = {}, path = window.location.pathname) => {
  const token = localStorage.getItem('token');
  const nextHeaders = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (token) {
    nextHeaders.Authorization = `Bearer ${token}`;
  }

  const viewedCompanyID = getViewedCompanyIDFromPath(path);
  if (viewedCompanyID) {
    nextHeaders['X-Company-ID'] = viewedCompanyID;
  }

  return nextHeaders;
};

// Helper for authenticated requests
const fetchWithAuth = async (url, options = {}) => {
  const headers = buildAuthHeaders(options.headers);

  const config = {
    ...options,
    headers,
  };

  const response = await fetch(url, config);

  if (response.status === 401) {
    // Token expired or invalid
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login'; // Force redirect to login
    throw new Error('Unauthorized');
  }

  return response;
};

const requireSuccessfulResponse = async (response) => {
  if (response.ok) return response;
  const errorText = await response.text();
  throw new Error(errorText.trim() || `HTTP error! status: ${response.status}`);
};

// Login Function
export const loginUser = async (credentials) => {
  const response = await fetch(`${API_BASE_URL}/api/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Login failed');
  }

  const data = await response.json();
  // Save token and user info
  localStorage.setItem('token', data.token);
  localStorage.setItem('user', JSON.stringify({
    name: data.name,
    role: data.role,
    companyID: data.companyID,
    redirectPath: data.redirectPath,
    isFirstLogin: Boolean(data.isFirstLogin),
  }));
  
  return data;
};

export const logoutUser = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/change-password`, {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText.trim() || '비밀번호 변경에 실패했습니다.');
  }
  return response.json();
};

export const getMachineryStatus = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.connected) {
    params.append('connected', 'true');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/api/machinery-status?${params.toString()}`);
  await requireSuccessfulResponse(response);
  return response.json();
};

export const buildWorkOrderQueryParams = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status && filters.status !== 'all') {
    params.append('status', filters.status);
  }
  if (filters.assigned && filters.assigned !== 'all') {
    params.append('assigned', filters.assigned);
  }
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.searchField && filters.searchField !== 'all') {
    params.append('searchField', filters.searchField);
  }
  if (filters.startDate) {
    params.append('startDate', filters.startDate);
  }
  if (filters.endDate) {
    params.append('endDate', filters.endDate);
  }
  const paginationRequested = filters.page !== undefined || filters.pageSize !== undefined;
  if (filters.page !== undefined) {
    params.append('page', filters.page);
  }
  if (filters.pageSize !== undefined) {
    params.append('pageSize', filters.pageSize);
  }
  if (paginationRequested) {
    params.append('includePagination', 'true');
  }
  return params;
};

export const getWorkOrders = async (filters = {}) => {
  const params = buildWorkOrderQueryParams(filters);
  const response = await fetchWithAuth(`${API_BASE_URL}/api/work-orders?${params.toString()}`);
  await requireSuccessfulResponse(response);
  return response.json();
};

export const createWorkOrder = async (workOrderData) => {
  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/work-orders`, {
      method: 'POST',
      body: JSON.stringify(workOrderData),
    });
    await requireSuccessfulResponse(response);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating work order:", error);
    throw error;
  }
};

export const updateWorkOrder = async (id, updateData) => {
  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/work-orders/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText.trim() || `HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating work order:", error);
    throw error;
  }
};

export const getWorkOrderMachines = async () => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/work-order-machines`);
  await requireSuccessfulResponse(response);
  return response.json();
};

export const sendWorkOrder = async (id, expectedVersion, retryUnknownAttempt = false) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/work-orders/${encodeURIComponent(id)}/send`, {
    method: 'POST',
    body: JSON.stringify({ expectedVersion, retryUnknownAttempt }),
  });
  await requireSuccessfulResponse(response);
  return response.json();
};

export const completeWorkOrder = async (id, expectedVersion, completedQuantity) => {
  const body = { expectedVersion };
  if (completedQuantity !== undefined && completedQuantity !== null) body.completedQuantity = completedQuantity;
  const response = await fetchWithAuth(`${API_BASE_URL}/api/work-orders/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  await requireSuccessfulResponse(response);
  return response.json();
};

export const deleteWorkOrder = async (id) => {
  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/work-orders/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error deleting work order:", error);
    throw error;
  }
};

export const getMaterialTypes = async () => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/material-types`);
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.json();
};

export const getMaterialShapes = async () => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/material-shapes`);
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.json();
};

export const getAdminCompanyMaterialTypes = async (companyID) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies/${encodeURIComponent(companyID)}/material-types`);
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.json();
};

export const createAdminCompanyMaterialType = async (companyID, materialType) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies/${encodeURIComponent(companyID)}/material-types`, {
    method: 'POST',
    body: JSON.stringify(materialType),
  });
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.json();
};

export const updateAdminCompanyMaterialType = async (companyID, code, changes) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies/${encodeURIComponent(companyID)}/material-types/${encodeURIComponent(code)}`, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  });
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.json();
};

const adminMaterialShapesPath = (companyID, code = '') => (
  `${API_BASE_URL}/api/admin/companies/${encodeURIComponent(companyID)}/material-shapes${code === '' ? '' : `/${encodeURIComponent(code)}`}`
);

export const getAdminCompanyMaterialShapes = async (companyID) => {
  const response = await fetchWithAuth(adminMaterialShapesPath(companyID));
  await requireSuccessfulResponse(response);
  return response.json();
};

export const createAdminCompanyMaterialShape = async (companyID, shape) => {
  const response = await fetchWithAuth(adminMaterialShapesPath(companyID), {
    method: 'POST', body: JSON.stringify(shape),
  });
  await requireSuccessfulResponse(response);
  return response.json();
};

export const updateAdminCompanyMaterialShape = async (companyID, code, changes) => {
  const response = await fetchWithAuth(adminMaterialShapesPath(companyID, code), {
    method: 'PATCH', body: JSON.stringify(changes),
  });
  await requireSuccessfulResponse(response);
  return response.json();
};

const adminManualMachinesPath = (companyID, id = '') => (
  `${API_BASE_URL}/api/admin/companies/${encodeURIComponent(companyID)}/manual-machines${id ? `/${encodeURIComponent(id)}` : ''}`
);

export const getAdminManualMachines = async (companyID) => {
  const response = await fetchWithAuth(adminManualMachinesPath(companyID));
  await requireSuccessfulResponse(response);
  return response.json();
};

export const createAdminManualMachine = async (companyID, data) => {
  const response = await fetchWithAuth(adminManualMachinesPath(companyID), {
    method: 'POST',
    body: JSON.stringify(data),
  });
  await requireSuccessfulResponse(response);
  return response.json();
};

export const updateAdminManualMachine = async (companyID, id, data) => {
  const payload = {
    machineName: data.machineName,
    description: data.description,
  };
  const response = await fetchWithAuth(adminManualMachinesPath(companyID, id), {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  await requireSuccessfulResponse(response);
  return response.json();
};

export const deleteAdminManualMachine = async (companyID, id) => {
  const response = await fetchWithAuth(adminManualMachinesPath(companyID, id), { method: 'DELETE' });
  await requireSuccessfulResponse(response);
};


export const getAdminCompanies = async () => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const createAdminCompany = async (companyData) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies`, {
    method: 'POST',
    body: JSON.stringify(companyData),
  });
  if (!response.ok) {
    throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const getAdminCompanyUsers = async (companyID) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies/${companyID}/users`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const createAdminCompanyUser = async (companyID, userData) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies/${companyID}/users`, {
    method: 'POST',
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const updateAdminCompanyUser = async (companyID, accountID, userData) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies/${encodeURIComponent(companyID)}/users/${encodeURIComponent(accountID)}`, {
    method: 'PATCH',
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const resetAdminCompanyUserPassword = async (companyID, accountID) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies/${encodeURIComponent(companyID)}/users/${encodeURIComponent(accountID)}/reset-password`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const getAdminCompanyMachines = async (companyID) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies/${encodeURIComponent(companyID)}/machines`);
  if (!response.ok) {
    throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const createAdminCompanyMachine = async (companyID, machineData) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies/${encodeURIComponent(companyID)}/machines`, {
    method: 'POST',
    body: JSON.stringify(machineData),
  });
  if (!response.ok) {
    throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const updateAdminCompanyMachine = async (companyID, combinedID, machineData) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/companies/${encodeURIComponent(companyID)}/machines/${encodeURIComponent(combinedID)}`, {
    method: 'PATCH',
    body: JSON.stringify(machineData),
  });
  if (!response.ok) {
    throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export {
  buildGatewayPLCConfigPayload,
  DEFAULT_WORK_ORDER_REGISTERS,
  validateGatewayPLCConfig,
} from './gatewayPLCConfig';

export const getAdminGatewayConfigStatus = async (gatewayID) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gateways/${encodeURIComponent(gatewayID)}/config-status`);
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.json();
};

export const getAdminGatewayPLCConfigs = async (gatewayID, companyID = '') => {
  const params = new URLSearchParams();
  if (companyID) params.set('companyID', companyID);
  const query = params.toString();
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gateways/${encodeURIComponent(gatewayID)}/plcs${query ? `?${query}` : ''}`);
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.json();
};

export const exportAdminGatewayPLCConfigsYAML = async (gatewayID) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gateways/${encodeURIComponent(gatewayID)}/plcs.yaml`);
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.blob();
};

export const createAdminGatewayPLCConfig = async (gatewayID, data) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gateways/${encodeURIComponent(gatewayID)}/plcs`, {
    method: 'POST', body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.json();
};

export const updateAdminGatewayPLCConfig = async (gatewayID, id, data) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gateways/${encodeURIComponent(gatewayID)}/plcs/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.json();
};

export const deleteAdminGatewayPLCConfig = async (gatewayID, id, expectedVersion) => {
  const params = new URLSearchParams({ expectedVersion: String(expectedVersion) });
  const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gateways/${encodeURIComponent(gatewayID)}/plcs/${encodeURIComponent(id)}?${params.toString()}`, { method: 'DELETE' });
  if (!response.ok) throw new Error(await response.text() || `HTTP error! status: ${response.status}`);
  return response.json();
};

export const getWorkLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status && filters.status !== 'all') {
    params.append('status', filters.status);
  }
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.machineName) {
    params.append('machineName', filters.machineName);
  }
  if (filters.page !== undefined) {
    params.append('page', filters.page);
  }
  if (filters.pageSize !== undefined) {
    params.append('pageSize', filters.pageSize);
  }
  if (filters.page !== undefined || filters.pageSize !== undefined) {
    params.append('includePagination', 'true');
  }
  if (filters.startDate) {
    params.append('startDate', filters.startDate);
  }
  if (filters.endDate) {
    params.append('endDate', filters.endDate);
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/api/work-logs?${params.toString()}`);
  await requireSuccessfulResponse(response);
  return response.json();
};

export const updateWorkLogMemo = async (id, memo) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/work-logs/${encodeURIComponent(id)}/memo`, {
    method: 'PATCH',
    body: JSON.stringify({ memo }),
  });
  await requireSuccessfulResponse(response);
  return response.json();
};

export const deleteWorkLog = async (id) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/work-logs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  await requireSuccessfulResponse(response);
};
